import { useEffect, useRef } from 'react'
import { getActiveTurn } from '@/lib/api'
import { useAgentStore } from '@/stores/agent-store'

const INITIAL_INTERVAL_MS = 2_500
const MAX_INTERVAL_MS = 10_000
const BACKOFF_FACTOR = 1.6

/**
 * Polls the backend for an in-flight turn belonging to `sessionId`. When the
 * turn transitions to a terminal state (completed / failed / cancelled — or
 * reaped as stale), calls `reconcileResumedTurn` on the agent store to
 * re-hydrate final state.
 *
 * Runs only when both `sessionId` and `turnId` are provided. Intended to be
 * mounted from the AI panel when the store reports `streamRecoveryState ===
 * 'recovering'` after a section switch.
 *
 * Stops on unmount and on terminal detection. Errors back off up to
 * MAX_INTERVAL_MS — transient network issues don't tear down the poller.
 */
export function useActiveTurnPoller(
  sessionId: string | null,
  turnId: string | null
): void {
  const reconcile = useAgentStore((state) => state.reconcileResumedTurn)
  const stoppedRef = useRef(false)

  useEffect(() => {
    if (!sessionId || !turnId) return

    stoppedRef.current = false
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let intervalMs = INITIAL_INTERVAL_MS

    const poll = async (): Promise<void> => {
      if (stoppedRef.current) return
      try {
        const response = await getActiveTurn(sessionId)
        if (stoppedRef.current) return

        const active = response.turn
        if (!active || active.turnId !== turnId) {
          stoppedRef.current = true
          await reconcile(turnId)
          return
        }
        intervalMs = INITIAL_INTERVAL_MS
      } catch {
        intervalMs = Math.min(intervalMs * BACKOFF_FACTOR, MAX_INTERVAL_MS)
      }

      if (stoppedRef.current) return
      timeoutId = setTimeout(poll, intervalMs)
    }

    timeoutId = setTimeout(poll, INITIAL_INTERVAL_MS)

    return () => {
      stoppedRef.current = true
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    }
  }, [sessionId, turnId, reconcile])
}
